import { useEffect, useRef, useState } from 'react';
import { leadCategoryLabels, nextActionLabels, userIntentLabels } from '@schwarz/contract';

import { useGetInteractionQuery } from '../../store/interactions-api.js';
import { IconClock, IconDoc, IconX } from '../../ui/icons.js';
import { initials } from '../../ui/initials.js';
import { when } from '../../ui/when.js';
import { CategoryChip } from './CategoryChip.js';
import { formatScore, magnitudePercent, toneOf } from './sentiment.js';
import styles from './InteractionDrawer.module.css';

interface Props {
  /** The interaction to show, or null for a closed drawer. */
  interactionId: string | null;
  onClose: () => void;
}

/**
 * One interaction, in detail (S08).
 *
 * The transcript is fetched with the record but hidden until asked for, and
 * showing it widens the panel rather than pushing the summary out of view — the
 * two are read together, which is the point of putting the analyzer's
 * conclusions beside the words they were drawn from.
 */
export function InteractionDrawer({ interactionId, onClose }: Props): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLElement>(null);

  const { data, isLoading, isError } = useGetInteractionQuery(interactionId ?? '', {
    skip: interactionId === null,
  });

  // Every record opens collapsed. Carrying the previous one's expanded state
  // over would show a transcript the operator did not ask for, scrolled to
  // wherever the last one happened to be.
  useEffect(() => {
    setExpanded(false);
  }, [interactionId]);

  /**
   * Move focus into the panel when it opens, and put it back when it closes.
   *
   * A modal that covers the page while focus stays on the row behind it is both
   * an accessibility failure and the source of the artefact on touch devices:
   * the tapped row keeps focus, the panel slides over it, and the focus ring
   * ends up drawn around everything.
   */
  useEffect(() => {
    if (interactionId === null) return;

    const previous = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();

    return () => {
      // Only if focus is still inside the panel: if the operator has clicked
      // somewhere else, taking it back would be the rudest thing we could do.
      if (document.activeElement === null || panelRef.current?.contains(document.activeElement)) {
        previous?.focus();
      }
    };
  }, [interactionId]);

  /**
   * Escape closes it.
   *
   * Absent from the mock, which is the gap worth closing rather than copying: a
   * panel that covers the page and traps the reader is the one thing every user
   * tries Escape on first.
   */
  useEffect(() => {
    if (interactionId === null) return;

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [interactionId, onClose]);

  const open = interactionId !== null;
  // Only for the score's colour now. The centred bar lives in the list, where a
  // column of them can be scanned; here the signed number says it exactly.
  const tone = data ? toneOf(data.sentiment.score) : 'neutral';

  return (
    <>
      <div
        className={`${styles.backdrop} ${open ? styles.shown : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        ref={panelRef}
        className={`${styles.drawer} ${open ? styles.shown : ''} ${expanded ? styles.wide : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Interaction detail"
        // Hidden from assistive tech when closed: it stays mounted so the slide
        // transition has something to animate, and a screen reader should not
        // find a panel that is off-screen.
        aria-hidden={!open}
      >
        {open && (
          <>
            <header className={styles.head}>
              <button
                ref={closeRef}
                type="button"
                className={styles.close}
                onClick={onClose}
                aria-label="Close"
              >
                <IconX width="18" height="18" />
              </button>

              {isLoading && <p className={styles.state}>Loading…</p>}
              {isError && (
                <p className={styles.state}>
                  That interaction could not be loaded. It may have been removed.
                </p>
              )}

              {data && (
                <>
                  <div className={styles.who}>
                    <div className={styles.av} aria-hidden="true">
                      {initials(data.contact.name)}
                    </div>
                    <div>
                      <b>{data.contact.name}</b>
                      <small>
                        {data.contact.company ?? '—'} ·{' '}
                        <span className={styles.phone}>{data.contact.phone}</span>
                      </small>
                    </div>
                  </div>

                  <div className={styles.chips}>
                    <CategoryChip category={data.leadCategory} />
                    {data.contact.phone && data.email && (
                      <a className={styles.mail} href={`mailto:${data.email}`}>
                        {data.email}
                      </a>
                    )}
                  </div>

                  <div className={styles.when}>
                    <IconClock width="13" height="13" />
                    Call time · {when(data.callAt ?? data.analyzedAt)}
                  </div>
                </>
              )}
            </header>

            {data && (
              <div className={styles.body}>
                <div className={styles.main}>
                  {data.mappingWarnings.length > 0 && (
                    /* Shown rather than hidden: a row that looks odd is easier to
                       trust once you can see the analyzer had to fall back. */
                    <section className={styles.warnings}>
                      <h4>Read with care</h4>
                      <ul>
                        {data.mappingWarnings.map((warning) => (
                          <li key={warning}>{warning}</li>
                        ))}
                      </ul>
                    </section>
                  )}

                  <section className={styles.sec}>
                    <h4>Sentiment</h4>
                    <div className={styles.kv}>
                      <span>Tone</span>
                      <b>{data.sentiment.toneLabel ?? '—'}</b>
                    </div>
                    <div className={styles.kv}>
                      <span>Score (−1.0 → +1.0)</span>
                      <b className={styles[tone]}>{formatScore(data.sentiment.score)}</b>
                    </div>
                    <div className={styles.kv}>
                      <span>Magnitude</span>
                      <b>{data.sentiment.magnitude.toFixed(2)}</b>
                    </div>
                    <div className={styles.gauge} aria-hidden="true">
                      <span style={{ width: `${magnitudePercent(data.sentiment.magnitude)}%` }} />
                    </div>
                  </section>

                  <section className={styles.sec}>
                    <h4>Outcome</h4>
                    <div className={styles.kv}>
                      <span>Lead category</span>
                      <b>{leadCategoryLabels[data.leadCategory]}</b>
                    </div>
                    <div className={styles.kv}>
                      <span>Caller intent</span>
                      <b>{userIntentLabels[data.userIntent]}</b>
                    </div>
                    <div className={styles.kv}>
                      <span>Next action</span>
                      <b className={styles.action}>{nextActionLabels[data.nextAction]}</b>
                    </div>
                  </section>

                  <section className={styles.sec}>
                    <h4>Agent summary &amp; transcript</h4>
                    <div className={styles.notes}>
                      {data.summary ?? 'The analyzer produced no summary for this call.'}
                    </div>
                    <button
                      type="button"
                      className={`${styles.btn} ${expanded ? styles.btnOn : ''}`}
                      disabled={data.transcript.length === 0}
                      title={
                        data.transcript.length === 0
                          ? 'No transcript was stored for this call'
                          : undefined
                      }
                      onClick={() => setExpanded((value) => !value)}
                    >
                      <IconDoc width="14" height="14" />
                      {data.transcript.length === 0
                        ? 'No transcript'
                        : expanded
                          ? 'Hide transcript'
                          : 'View full transcript'}
                    </button>
                  </section>
                </div>

                {expanded && data.transcript.length > 0 && (
                  <div className={styles.transcript}>
                    <div className={styles.transcriptHead}>
                      <h4>Full transcript</h4>
                      <span>{data.transcript.length} turns · agent &amp; contact</span>
                    </div>
                    <div className={styles.lines}>
                      {data.transcript.map((turn) => (
                        <div
                          key={turn.seq}
                          className={`${styles.line} ${turn.role === 'AGENT' ? styles.agent : styles.contact}`}
                        >
                          <div className={styles.speaker}>
                            {turn.role === 'AGENT' ? 'Agent' : data.contact.name}
                          </div>
                          <div className={styles.bubble}>{turn.content}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </aside>
    </>
  );
}
